import type { FC, ReactNode } from 'react'

import { useCallback, useEffect, useRef } from 'react'
import { X } from 'lucide-react'

/** a neutral page the visitor lands on, replacing this one in the history */
const EXIT_URL = 'https://www.google.com.br/search?q=previs%C3%A3o+do+tempo'
const DOUBLE_PRESS_MS = 800
const RESTORE_MS = 4000

/**
 * Safety exit, present on every page. One click (or Esc twice) hides the page
 * at once and swaps it for a neutral site, so whoever is nearby sees nothing
 * and the back button does not bring this page back.
 */
export const QuickExit: FC = (): ReactNode => {
    const lastEscape = useRef<number>(0)

    const leave = useCallback((): void => {
        const html = document.documentElement
        const title = document.title

        html.style.visibility = 'hidden'
        document.title = "Previsão do tempo"
        window.location.replace(EXIT_URL)

        // if the browser kept this page (a blocked or slow redirect), give it back
        window.setTimeout(() => {
            html.style.visibility = ''
            document.title = title
        }, RESTORE_MS)
    }, [])

    useEffect(() => {
        // coming back through the history cache must never show a hidden page
        const onShow = (): void => {
            document.documentElement.style.visibility = ''
        }

        window.addEventListener('pageshow', onShow)

        return () => window.removeEventListener('pageshow', onShow)
    }, [])

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return

            const now = performance.now()

            if (now - lastEscape.current < DOUBLE_PRESS_MS) {
                leave()

                return
            }

            lastEscape.current = now
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [leave])

    return (
        <button
            type='button'
            className='qx-btn'
            onClick={leave}
            aria-label="Sair rápido deste site. Atalho: tecla Esc duas vezes"
            title="Sai deste site na hora. Atalho: Esc duas vezes"
        >
            <X className='size-4' strokeWidth={1.8} aria-hidden='true' />

            <span>
                Sair rápido
            </span>
        </button>
    )
}
